import React, { useState, useEffect } from 'react';
import './Calender.css';
import axiosInstance from '../../../utils/axiosInstance';
import { AddHoliday } from '../addHoliday/addHoliday';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import { Col, Row } from 'react-bootstrap';

export const Calender = ({ employeeId }) => {

    const { t } = useTranslation();

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [events, setEvents] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [companyId, setCompanyId] = useState(null);
    const [showPlaceholder, setShowPlaceholder] = useState(true);
    const [selectedHoliday, setSelectedHoliday] = useState(null);

    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                const response = await axiosInstance.get(`/admin/holidays/${employeeId}`);
                const { companyId, holidayResults } = response.data;
                setCompanyId(companyId);

                const holidaysData = holidayResults.map(holiday => ({
                    date: new Date(holiday.holiday_date),
                    occasion: holiday.holiday_occasion,
                    branchId: holiday.branch_id,
                    holidayId: holiday.holiday_id
                }));
                setHolidays(holidaysData);
            } catch (error) {
                // Check if error response is available
                if (!error.response) {
                    // Network error or no response
                    toast.error("Looks like you are offline. Please check your internet connectivity.");
                } else {
                    console.error('Error fetching holidays', error);
                }
            }
        };

        fetchHolidays();
    }, [employeeId]);

    const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const getEventsForDay = (day) => {
        const eventOfDay = events.find(event =>
            event.date.getDate() === day.getDate() &&
            event.date.getMonth() === day.getMonth() &&
            event.date.getFullYear() === day.getFullYear()
        );
        return eventOfDay ? eventOfDay.events : [];
    };

    const isHoliday = (day) => {
        return holidays.find(holiday =>
            holiday.date.getDate() === day.getDate() &&
            holiday.date.getMonth() === day.getMonth() &&
            holiday.date.getFullYear() === day.getFullYear()
        );
    };

    const handleDayClick = (date) => {
        setSelectedDate(date);
        const holiday = isHoliday(date);
        setSelectedHoliday(holiday);
        setShowPlaceholder(false);
    };

    const isSameDay = (date1, date2) =>
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();

    const renderDays = () => {
        const totalDays = daysInMonth(currentDate);
        const startingDay = firstDayOfMonth(currentDate);
        const days = [];

        for (let i = 0; i < startingDay; i++) {
            days.push(<div key={`empty-${i}`} className="empty-cell"></div>);
        }

        for (let i = 1; i <= totalDays; i++) {
            const day = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const eventsForDay = getEventsForDay(day);
            const holiday = isHoliday(day);

            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            const weekendStyle = isWeekend ? 'weekend' : '';

            const classNames = `day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${weekendStyle} ${holiday ? 'holiday' : ''}`;
            days.push(
                <div key={i} className={classNames} onClick={() => handleDayClick(day)}>
                    <div>{i}</div>
                    {holiday && (
                        <div className="holiday-indicator">
                            <span
                                className="holiday-occasion"
                                data-tooltip-id={`holiday-tooltip-${holiday.holidayId}`}
                                data-tooltip-content={holiday.occasion}
                            >
                                {holiday.occasion.length > 10 ? `${holiday.occasion.substring(0, 10)}...` : holiday.occasion}
                            </span>
                            <Tooltip id={`holiday-tooltip-${holiday.holidayId}`} className="custom-tooltip" />
                        </div>
                    )}
                    <div className="allEvent">
                        {eventsForDay.map((event, index) => (
                            <div key={index} className="event">{event.title}</div>
                        ))}
                    </div>
                </div>
            );
        }

        return days;
    };

    const goToPreviousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    const goToNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

    return (
        <div className="calendar-body">
            <Row>
                <Col lg={6} md={12} sm={12} style={{ margin: '10px 0' }}>
                    <div className="calendar-container" data-aos="fade-up">
                        <div className="calendar">
                            <div className="header">
                                <button onClick={goToPreviousMonth} className="previousMonthBtn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-chevron-compact-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M9.224 1.553a.5.5 0 0 1 .223.67L6.56 8l2.888 5.776a.5.5 0 1 1-.894.448l-3-6a.5.5 0 0 1 0-.448l3-6a.5.5 0 0 1 .67-.223" />
                                    </svg>
                                </button>
                                <span>{t(`months.${currentDate.toLocaleDateString('en-US', { month: 'long' }).toLocaleLowerCase()}`)} {currentDate.toLocaleDateString('en-US', { year: 'numeric' })}</span>
                                <button onClick={goToNextMonth} className="nextMonthBtn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-chevron-compact-right" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M6.776 1.553a.5.5 0 0 1 .671.223l3 6a.5.5 0 0 1 0 .448l-3 6a.5.5 0 1 1-.894-.448L9.44 8 6.553 2.224a.5.5 0 0 1 .223-.671" />
                                    </svg>
                                </button>
                            </div>
                            <div className="days">
                                <div className="weekdays">{t('weekdays.sunday')}</div>
                                <div className="weekdays">{t('weekdays.monday')}</div>
                                <div className="weekdays">{t('weekdays.tuesday')}</div>
                                <div className="weekdays">{t('weekdays.wednesday')}</div>
                                <div className="weekdays">{t('weekdays.thursday')}</div>
                                <div className="weekdays">{t('weekdays.friday')}</div>
                                <div className="weekdays">{t('weekdays.saturday')}</div>
                                {renderDays()}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col lg={6} md={12} sm={12} style={{ margin: '10px 0' }}>
                    <div className='container'>
                        {showPlaceholder && (
                            <div className="placeholder-image-container" data-aos="fade-left">
                                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="currentColor" className="bi bi-calendar4-week" viewBox="0 0 16 16">
                                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v1h14V3a1 1 0 0 0-1-1zm13 3H1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z" />
                                    <path d="M11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-2 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                                </svg>
                                <div className='mt-2' style={{ fontSize: '14px' }}>{t("manageHolidays_selectDate")}</div>
                            </div>
                        )}
                        {selectedDate && (
                            <div className="regularize-form-container">
                                <AddHoliday selectedDate={selectedDate} holiday={selectedHoliday} companyId={companyId} />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};
